import { eventBannerThumb } from '../../../utils/media';
// ---- blueprint / retro-console HUD palette ----
const NAVY = '#0a1428';        // card background (dark navy)
const NAVY_HI = '#0f1d3a';     // raised panel / thumbnail backdrop
const GRID = '#1c3056';        // faint grid / hairline blue
const ACCENT = '#3a7bff';      // bright blueprint accent
const LIGHT = '#9fb6e0';       // light-blue body text
const LIGHT_DIM = '#5c719c';   // dim label text
const INK = '#e8f0ff';         // near-white title ink

const CARD_HEIGHT = 212;
const SIDEBAR_W = 34;
const TOPBAR_H = 16;

const fmtDate = (d) =>
  (d ? new Date(d).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }) : '');
const fmtTime = (d) =>
  (d ? new Date(d).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' }) : '');

// ---- small inline blueprint glyphs (generic — no trademarked marks) ----
function PinIcon({ size = 11, color = LIGHT }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2 C8 2 5 5 5 9 C5 14 12 22 12 22 C12 22 19 14 19 9 C19 5 16 2 12 2 Z" fill="none" stroke={color} strokeWidth={1.6} />
      <circle cx={12} cy={9} r={2.4} fill="none" stroke={color} strokeWidth={1.6} />
    </svg>
  );
}
function CalIcon({ size = 11, color = LIGHT }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <rect x={3.5} y={5} width={17} height={15.5} rx={2} fill="none" stroke={color} strokeWidth={1.6} />
      <line x1={3.5} y1={9.5} x2={20.5} y2={9.5} stroke={color} strokeWidth={1.6} />
      <line x1={8} y1={3} x2={8} y2={6.5} stroke={color} strokeWidth={1.6} strokeLinecap="round" />
      <line x1={16} y1={3} x2={16} y2={6.5} stroke={color} strokeWidth={1.6} strokeLinecap="round" />
    </svg>
  );
}
function PeopleIcon({ size = 11, color = LIGHT }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle cx={9} cy={8} r={3.2} fill="none" stroke={color} strokeWidth={1.6} />
      <path d="M3.5 20 C3.5 16 6 14 9 14 C12 14 14.5 16 14.5 20" fill="none" stroke={color} strokeWidth={1.6} />
      <path d="M16 14 C18.5 14 20.5 16 20.5 19.5" fill="none" stroke={color} strokeWidth={1.6} />
      <circle cx={16.5} cy={8.5} r={2.6} fill="none" stroke={color} strokeWidth={1.6} />
    </svg>
  );
}
function GlobeIcon({ size = 14, color = LIGHT_DIM }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle cx={12} cy={12} r={9} fill="none" stroke={color} strokeWidth={1.3} />
      <line x1={3} y1={12} x2={21} y2={12} stroke={color} strokeWidth={1} />
      <path d="M12 3 C7 7 7 17 12 21 M12 3 C17 7 17 17 12 21" fill="none" stroke={color} strokeWidth={1} />
    </svg>
  );
}
// Tiny vertical-bar "barcode" (decorative).
function Barcode({ widths, h = 9, color = LIGHT }) {
  let cx = 0;
  return (
    <svg width={widths.reduce((a, b) => a + b + 1.2, 0)} height={h} xmlns="http://www.w3.org/2000/svg">
      {widths.map((bw, i) => {
        const el = <rect key={i} x={cx} y={0} width={bw} height={h} fill={color} opacity={i % 2 ? 0.5 : 0.9} />;
        cx += bw + 1.2;
        return el;
      })}
    </svg>
  );
}

// Corner-bracket frame drawn AROUND the thumbnail (never over the image).
function CornerBrackets({ w, h, color = ACCENT, len = 14, sw = 1.4, inset = 2 }) {
  const a = inset, b = inset + len;
  return (
    <svg width={w} height={h} xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
      <path d={`M ${a} ${b} L ${a} ${a} L ${b} ${a}`} fill="none" stroke={color} strokeWidth={sw} />
      <path d={`M ${w - b} ${a} L ${w - a} ${a} L ${w - a} ${b}`} fill="none" stroke={color} strokeWidth={sw} />
      <path d={`M ${a} ${h - b} L ${a} ${h - a} L ${b} ${h - a}`} fill="none" stroke={color} strokeWidth={sw} />
      <path d={`M ${w - b} ${h - a} L ${w - a} ${h - a} L ${w - a} ${h - b}`} fill="none" stroke={color} strokeWidth={sw} />
      {/* registration crosshair mid-edges */}
      <path d={`M ${w / 2 - 4} ${a} L ${w / 2 + 4} ${a} M ${w / 2} ${a - 0} L ${w / 2} ${a + 6}`} stroke={color} strokeWidth={0.8} opacity={0.7} />
    </svg>
  );
}

// Faint blueprint grid + CRT scanlines + outer brackets + edge ticks (stretches
// with the card via preserveAspectRatio="none" — only stretch-tolerant marks).
function CardHud() {
  const W = 320, H = CARD_HEIGHT, step = 26;
  const vlines = []; for (let x = step; x < W; x += step) vlines.push(x);
  const hlines = []; for (let y = step; y < H; y += step) hlines.push(y);
  const scan = []; for (let y = 2; y < H; y += 4) scan.push(y);
  const ticks = []; for (let x = 14; x < W - 14; x += 12) ticks.push(x);
  const len = 16, o = 7;
  return (
    <svg width="100%" height="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
      <g opacity={0.5}>
        {vlines.map((x) => <line key={`v${x}`} x1={x} y1={0} x2={x} y2={H} stroke={GRID} strokeWidth={0.6} />)}
        {hlines.map((y) => <line key={`h${y}`} x1={0} y1={y} x2={W} y2={y} stroke={GRID} strokeWidth={0.6} />)}
      </g>
      {/* CRT scanlines */}
      <g opacity={0.06}>
        {scan.map((y) => <line key={`s${y}`} x1={0} y1={y} x2={W} y2={y} stroke="#bcd2ff" strokeWidth={1} />)}
      </g>
      {/* top + bottom edge ticks */}
      <g opacity={0.5}>
        {ticks.map((x) => <line key={`tt${x}`} x1={x} y1={3} x2={x} y2={6} stroke={LIGHT_DIM} strokeWidth={0.7} />)}
        {ticks.map((x) => <line key={`tb${x}`} x1={x} y1={H - 6} x2={x} y2={H - 3} stroke={LIGHT_DIM} strokeWidth={0.7} />)}
      </g>
      {/* outer corner brackets */}
      <g opacity={0.9}>
        <path d={`M ${o} ${o + len} L ${o} ${o} L ${o + len} ${o}`} fill="none" stroke={ACCENT} strokeWidth={1.2} />
        <path d={`M ${W - o - len} ${o} L ${W - o} ${o} L ${W - o} ${o + len}`} fill="none" stroke={ACCENT} strokeWidth={1.2} />
        <path d={`M ${o} ${H - o - len} L ${o} ${H - o} L ${o + len} ${H - o}`} fill="none" stroke={ACCENT} strokeWidth={1.2} />
        <path d={`M ${W - o - len} ${H - o} L ${W - o} ${H - o} L ${W - o} ${H - o - len}`} fill="none" stroke={ACCENT} strokeWidth={1.2} />
      </g>
    </svg>
  );
}

function SidebarMark({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2 L20 7 L20 17 L12 22 L4 17 L4 7 Z" fill="none" stroke={ACCENT} strokeWidth={1.4} />
      <line x1={12} y1={5} x2={12} y2={19} stroke={LIGHT} strokeWidth={0.9} opacity={0.7} />
      <line x1={6} y1={12} x2={18} y2={12} stroke={LIGHT} strokeWidth={0.9} opacity={0.7} />
      <circle cx={12} cy={12} r={2.2} fill="none" stroke={ACCENT} strokeWidth={1.2} />
    </svg>
  );
}

// A full custom card layout in a blueprint / retro-console HUD style.
export default function BlueprintLayout({ event }) {
  const dateStr = event?.eventDate ? `${fmtDate(event.eventDate)}` : 'DATE TBA';
  const timeStr = event?.eventDate ? fmtTime(event.eventDate) : '';
  const cat = (event?.category || 'EVENT').toString().toUpperCase();
  const going = event?.attendeeCount ?? 0;
  const interested = event?.interestedCount ?? 0;
  const attendingLabel = going > 0 ? `${going}+ ATTENDING` : interested > 0 ? `${interested}+ INTERESTED` : 'BE THE FIRST';
  const myStatus = event?.myStatus || null;
  const vlabel = (cat || 'NIGHT').slice(0, 10);

  return (
    <div style={styles.card}>
      <CardHud />

      {/* ---- TOP STATUS STRIP ---- */}
      <div style={styles.topbar}>
        <div style={styles.topMark} />
        <div style={styles.segs}>
          {[0, 1, 2, 3, 4, 5, 6].map((i) => <div key={i} style={{ ...styles.seg, ...(i < 3 ? { backgroundColor: ACCENT } : {}) }} />)}
        </div>
        <span style={styles.topLabel}>EVENT CARD</span>
        <div style={{ flex: 1 }} />
        <Barcode widths={[1, 2, 1, 1, 2, 1, 3, 1, 1, 2]} h={8} color={LIGHT} />
        <span style={styles.topCode}>00-26 ///</span>
      </div>

      <div style={styles.row}>
        {/* ---- LEFT SIDEBAR ---- */}
        <div style={styles.sidebar}>
          <div style={styles.sidebarMark}><SidebarMark size={18} /></div>
          <div style={styles.vlabelWrap}>
            <span style={{ ...styles.vlabel, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{vlabel}</span>
          </div>
          <div style={styles.dots}>
            <div style={{ ...styles.dot, backgroundColor: ACCENT }} />
            <div style={{ ...styles.dot, backgroundColor: LIGHT_DIM }} />
            <div style={{ ...styles.dot, backgroundColor: LIGHT_DIM }} />
          </div>
          {/* mini spec panel */}
          <div style={styles.specPanel}>
            <span style={styles.specTxt}>NTSC</span>
            <span style={styles.specTxt}>J</span>
            <div style={styles.specRule} />
            <span style={styles.specTxt}>Y2K</span>
          </div>
          <GlobeIcon size={13} color={LIGHT_DIM} />
        </div>

        {/* ---- MAIN COLUMN ---- */}
        <div style={styles.main}>
          <div style={styles.catPill}>
            <div style={styles.catDot} />
            <span style={{ ...styles.catText, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cat}</span>
            {event && event.isPublic === false ? <span style={styles.privText}>· PRIVATE</span> : null}
          </div>

          <span style={{ ...styles.title, display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden' }}>{(event?.title || 'UNTITLED EVENT').toUpperCase()}</span>
          <div style={styles.titleRule} />

          <div style={styles.metaRow}>
            <CalIcon size={11} color={LIGHT} />
            <span style={{ ...styles.metaText, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{dateStr}{timeStr ? `  ·  ${timeStr}` : ''}</span>
          </div>
          {event?.address ? (
            <div style={styles.metaRow}>
              <PinIcon size={11} color={LIGHT} />
              <span style={{ ...styles.metaText, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{event.address}</span>
            </div>
          ) : null}
          <div style={styles.metaRow}>
            <PeopleIcon size={11} color={LIGHT} />
            <span style={{ ...styles.metaText, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{attendingLabel}</span>
          </div>

          <div style={styles.rsvpRow}>
            <div style={{ ...styles.rsvpPill, ...(myStatus === 'attending' ? styles.rsvpPillActive : {}) }}>
              <span style={{ ...styles.rsvpText, ...(myStatus === 'attending' ? styles.rsvpTextActive : {}) }}>GOING</span>
            </div>
            <div style={{ ...styles.rsvpPill, ...(myStatus === 'interested' ? styles.rsvpPillActive : {}) }}>
              <span style={{ ...styles.rsvpText, ...(myStatus === 'interested' ? styles.rsvpTextActive : {}) }}>INTERESTED</span>
            </div>
          </div>
        </div>

        {/* ---- RIGHT THUMBNAIL SLOT (image untouched) ---- */}
        <div style={styles.thumbSlot}>
          {event?.bannerImageUrl ? (
            <img src={eventBannerThumb(event)} alt="" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <div style={styles.thumbPlaceholder}>
              <svg width="100%" height="100%" viewBox="0 0 100 200" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                <g opacity={0.55}>
                  {[20, 40, 60, 80].map((x) => <line key={`pv${x}`} x1={x} y1={0} x2={x} y2={200} stroke={GRID} strokeWidth={0.7} />)}
                  {[25, 50, 75, 100, 125, 150, 175].map((y) => <line key={`ph${y}`} x1={0} y1={y} x2={100} y2={y} stroke={GRID} strokeWidth={0.7} />)}
                  <path d="M 18 130 L 50 70 L 82 130" fill="none" stroke={ACCENT} strokeWidth={1.4} />
                </g>
              </svg>
              <span style={styles.thumbHint}>ADD THUMBNAIL</span>
            </div>
          )}
          <CornerBrackets w={134} h={CARD_HEIGHT - TOPBAR_H} color={ACCENT} />
          {/* tiny spec readouts at the thumbnail corners (around, not over center) */}
          <span style={styles.zoomTL}>00-26</span>
          <span style={styles.zoomBR}>ZOOM 96.2{'\n'}FOCUS AUTO</span>
          <div style={styles.thumbSeam} />
        </div>
      </div>

      {/* ---- FOOTER ---- */}
      <div style={styles.footer}>
        <div style={styles.progressTrack}>
          <div style={styles.progressFill} />
          <div style={styles.progressNode} />
        </div>
        <div style={styles.footerRow}>
          <span style={styles.footerText}>LIVE THE NIGHT</span>
          <span style={styles.footerSub}>NO LIMITS</span>
        </div>
      </div>
    </div>
  );
}

const styles = {
  card: { display: 'flex', flexDirection: 'column', position: 'relative', width: '100%', height: CARD_HEIGHT, borderRadius: 12, overflow: 'hidden', backgroundColor: NAVY, border: `1px solid ${GRID}` },

  // top status strip
  topbar: { display: 'flex', flexDirection: 'row', height: TOPBAR_H, alignItems: 'center', paddingLeft: 8, paddingRight: 8, gap: 6, borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: GRID },
  topMark: { display: 'flex', flexDirection: 'column', width: 6, height: 6, borderRadius: 1, backgroundColor: ACCENT },
  segs: { display: 'flex', flexDirection: 'row', gap: 2 },
  seg: { display: 'flex', flexDirection: 'column', width: 5, height: 3, backgroundColor: GRID, borderRadius: 0.5 },
  topLabel: { color: LIGHT, fontSize: 7.5, fontWeight: '800', letterSpacing: 2 },
  topCode: { color: ACCENT, fontSize: 7.5, fontWeight: '800', letterSpacing: 1 },

  row: { display: 'flex', flexDirection: 'row', flex: 1 },

  // sidebar
  sidebar: { display: 'flex', flexDirection: 'column', width: SIDEBAR_W, paddingTop: 10, paddingBottom: 10, alignItems: 'center', justifyContent: 'space-between', borderRightWidth: 1, borderRightStyle: 'solid', borderRightColor: GRID },
  sidebarMark: { display: 'flex', flexDirection: 'column', opacity: 0.95 },
  vlabelWrap: { display: 'flex', flexDirection: 'column', flex: 1, alignItems: 'center', justifyContent: 'center' },
  vlabel: { color: LIGHT_DIM, fontSize: 9, fontWeight: '800', letterSpacing: 3, transform: 'rotate(-90deg)', width: 120, textAlign: 'center' },
  dots: { display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center', marginTop: 4, marginBottom: 4 },
  dot: { display: 'flex', flexDirection: 'column', width: 4, height: 4, borderRadius: 2 },
  specPanel: { display: 'flex', flexDirection: 'column', alignItems: 'center', borderWidth: 1, borderStyle: 'solid', borderColor: GRID, borderRadius: 3, paddingLeft: 3, paddingRight: 3, paddingTop: 3, paddingBottom: 3, gap: 1.5, marginBottom: 6 },
  specTxt: { color: LIGHT_DIM, fontSize: 6.5, fontWeight: '800', letterSpacing: 0.5 },
  specRule: { display: 'flex', flexDirection: 'column', width: 14, height: 1, backgroundColor: GRID, marginTop: 1, marginBottom: 1 },

  // main column
  main: { display: 'flex', flexDirection: 'column', flex: 1, paddingLeft: 11, paddingRight: 11, paddingTop: 9, paddingBottom: 6, justifyContent: 'flex-start' },
  catPill: { display: 'flex', flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start', borderWidth: 1, borderStyle: 'solid', borderColor: ACCENT + '66', backgroundColor: ACCENT + '1f', borderRadius: 4, paddingLeft: 6, paddingRight: 6, paddingTop: 2, paddingBottom: 2, gap: 5, maxWidth: '100%' },
  catDot: { display: 'flex', flexDirection: 'column', width: 5, height: 5, borderRadius: 1, backgroundColor: ACCENT },
  catText: { color: LIGHT, fontSize: 9, fontWeight: '800', letterSpacing: 1.5 },
  privText: { color: LIGHT_DIM, fontSize: 9, fontWeight: '800', letterSpacing: 1 },
  title: { color: INK, fontSize: 18, lineHeight: '20px', fontWeight: '800', letterSpacing: 0.5, marginTop: 6, marginBottom: 4 },
  titleRule: { display: 'flex', flexDirection: 'column', height: 1, width: 34, backgroundColor: ACCENT, marginBottom: 6 },
  metaRow: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 3 },
  metaText: { color: LIGHT, fontSize: 11, fontWeight: '600', letterSpacing: 0.4, flexShrink: 1 },
  rsvpRow: { display: 'flex', flexDirection: 'row', gap: 7, marginTop: 9 },
  rsvpPill: { display: 'flex', flexDirection: 'column', borderWidth: 1, borderStyle: 'solid', borderColor: GRID, backgroundColor: NAVY_HI, borderRadius: 4, paddingLeft: 9, paddingRight: 9, paddingTop: 4, paddingBottom: 4 },
  rsvpPillActive: { borderColor: 'rgba(255,255,255,0.85)', backgroundColor: 'rgba(255,255,255,0.12)' },
  rsvpText: { color: LIGHT_DIM, fontSize: 9, fontWeight: '800', letterSpacing: 1 },
  rsvpTextActive: { color: '#ffffff' },

  // thumbnail slot
  thumbSlot: { display: 'flex', flexDirection: 'column', width: '40%', height: '100%', backgroundColor: NAVY_HI, position: 'relative', overflow: 'hidden' },
  thumbSeam: { position: 'absolute', left: 0, top: 0, bottom: 0, width: 1, backgroundColor: GRID },
  thumbPlaceholder: { display: 'flex', flexDirection: 'column', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, alignItems: 'center', justifyContent: 'center' },
  thumbHint: { position: 'absolute', color: LIGHT_DIM, fontSize: 9, fontWeight: '800', letterSpacing: 2 },
  zoomTL: { position: 'absolute', top: 6, left: 8, color: LIGHT, fontSize: 6.5, fontWeight: '800', letterSpacing: 1, opacity: 0.9 },
  zoomBR: { position: 'absolute', bottom: 6, right: 8, color: LIGHT, fontSize: 6, fontWeight: '800', letterSpacing: 0.5, textAlign: 'right', opacity: 0.9, whiteSpace: 'pre-line' },

  // footer
  footer: { display: 'flex', flexDirection: 'column', position: 'absolute', left: SIDEBAR_W + 11, right: '42%', bottom: 8 },
  progressTrack: { display: 'flex', flexDirection: 'column', position: 'relative', height: 2, backgroundColor: GRID, borderRadius: 1, marginRight: 8, marginBottom: 5, justifyContent: 'center' },
  progressFill: { position: 'absolute', left: 0, width: '42%', height: 2, backgroundColor: ACCENT, borderRadius: 1 },
  progressNode: { position: 'absolute', left: '42%', width: 4, height: 4, borderRadius: 2, backgroundColor: '#fff', top: -1 },
  footerRow: { display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginRight: 8 },
  footerText: { color: LIGHT_DIM, fontSize: 8, fontWeight: '800', letterSpacing: 2 },
  footerSub: { color: ACCENT, fontSize: 7.5, fontWeight: '800', letterSpacing: 1 },
};
